import React, { useMemo } from "react";
import {Space, Avatar, Popconfirm, Tooltip, Typography, Tag} from "antd";
import { PushpinOutlined, InfoCircleOutlined, StarOutlined, StarFilled } from "@ant-design/icons";

import {formatCompanyLogoUrl} from "../../../../api/companyApi";
import Logo from "../../../../components/Graphics/Logo";
import TargetAllocations from "../../../../components/TargetAllocations";
import {DoubleLine} from "../../../../components/Labels";
import TimeCell from "../../../../components/TimeCell";
import PortfolioRowMenu from "./PortfolioRowMenu";
import PropTypes from 'prop-types';
import CountryFlag from "../../../../components/Icons/CountryFlag";
import {PortfolioTypes } from "../../../../utils/strings"
import IconChip from "../../../../components/Chips/IconChip";
import TimeChip  from "../../../../components/TimeChip";
import { UserRound, SquareUserRound } from 'lucide-react';


const {Link} = Typography;



const MemoizedPortfolioRowMenu = React.memo(({ onEdit, portfolio }) => (
    <PortfolioRowMenu
        onEdit={onEdit}
        portfolio={portfolio}
    />
));

// Memoize the menu creation function
const CreatePortfolioMenu = React.memo(({ record, handleEdit }) => {
    const onEdit = React.useCallback(() => {
        handleEdit(record.id);
    }, [handleEdit, record.id]);

    return (
        <MemoizedPortfolioRowMenu
            onEdit={onEdit}
            portfolio={record}
        />
    );
});

// Make sure to add prop types
CreatePortfolioMenu.propTypes = {
    record: PropTypes.object.isRequired,
    handleEdit: PropTypes.func.isRequired
};



const FavoriteStar = ({ id, isStarred, toggleFavorite }) => {
    return isStarred ? (
    <Popconfirm
      title="Are you sure you want to remove this from favorites?"
      onConfirm={() => toggleFavorite(id, false)}
      okText="Yes"
      cancelText="No"
    >
      <StarFilled style={{ color: 'var(--color-primary)', cursor: 'pointer', fontSize: '18px' }} />
    </Popconfirm>
  ) : (
    <Popconfirm
      title="Are you sure you want to add this to favorites?"
      onConfirm={() => toggleFavorite(id, true)}
      okText="Yes"
      cancelText="No"
    >
      <StarOutlined style={{ cursor: 'pointer', fontSize: '18px', color: '#B6C7D6' }} />
    </Popconfirm>
  );
}

const getTagColors = (tagType) => {
    switch (tagType) {
        case 'asset_class':
            return 'bg-gradient-to-br from-blue-200 to-blue-100';
        case 'style':
            return 'bg-gradient-to-tr from-purple-200 to-purple-100';
        case 'size':
            return 'bg-gradient-to-b from-green-200 to-green-100';
        default:
            return 'bg-gradient-to-bl from-gray-200 to-gray-100';
    }
};
const TagPlaceholder = ({ type }) => (
    <div className="w-24 h-6 rounded opacity-20 bg-gradient-to-b from-gray-100 to-gray-50" />
);


export const commonTableColumns = ({ benchmarkTickers, showTargets=true })=>{

    const tableColumns =  [
        {
              title: 'Created',
              dataIndex: ['_meta', 'create_unix'],
              key: ['_meta', 'create_unix'],
              render: (timestamp, record) => {
                return <TimeChip timestamp={timestamp} />
              },
              sorter: (a, b) => new Date(b?._meta?.create_unix) - new Date(a?._meta?.create_unix),
            },
        {
              title: 'Updated',
              dataIndex: ['_meta', 'update_unix'],
              key: ['_meta', 'update_unix'],
              render: (timestamp, record) => {
                  if (!timestamp) return null;
                return <TimeChip timestamp={timestamp} showIcon={false} />
              },
              sorter: (a, b) => new Date(b?._meta?.create_unix) - new Date(a?._meta?.create_unix),
            }
    ];
    if (showTargets) {
        tableColumns.unshift({
              title: "Targets",
              dataIndex: ['targets', 'equity'],
              key: 'targets',
              render: (_, record)=> <TargetAllocations equity={record?.targets?.equity} fixed={record?.targets?.fixed} />,
                align: 'center'
        })
    }

    if (benchmarkTickers) {
        tableColumns.push( {
              title: 'Benchmark',
              dataIndex: 'benchmark',
              key: 'benchmark',
              render: (benchmark) => {
                  const benchmarkTicker = benchmarkTickers?.find(t => t.symbol === benchmark);
                  if (!benchmarkTicker) return <Space style={{width: 150}}>
                      <Avatar src={'https://storage.googleapis.com/finiat-web-static-resources/platformIcons/factor-logo.png'} size={36} />

                       <Typography.Text>{benchmark?.toUpperCase?.()??''}</Typography.Text>
                  </Space>
                  return <Space style={{width: 150}}>
                      <Avatar src={benchmarkTicker?.logo} size={36} />
                      <Typography.Text className={'text-grey-600 text-xs capitalize '}>{benchmarkTicker?.name}</Typography.Text>
                  </Space>
              },
              width: 175,
            })
    }

    return tableColumns;
}


/**
 *
 * @param handleEdit
 * @param toggleFavorite
 * @returns {[{title: string, render: (function(*, *): JSX.Element), key: string},{title: string, render: (function(*, *)), key: string},{title: string, render: (function(*, *)), key: string},{title: string, render: (function()), key: string},{dataIndex: string, title: string, key: string},null,null,null,null,null,null,null,null,null]}
 * @constructor
 */
const useTableColumns = ({onEdit, toggleFavorite, benchmarkTickers, setActiveFilters})=>{

    const typeColors = {
        b: 'bg-gray-400',
        s: 'bg-rose-400',
        m: 'bg-sky-500',
        mm: 'bg-gradient-to-br from-indigo-800 to-blue-600',
    }

    return React.useMemo(() => [
        {
                title: <Tooltip><InfoCircleOutlined size={16}/> </Tooltip>,
                key: 'action',
                render: (_, record) => (
                     <CreatePortfolioMenu
                            record={record}
                            handleEdit={onEdit}
                        />
                ),
                align: 'center',
            width: 75
            },
            {
                title: <Tooltip title={'Pin to Favorites'}><PushpinOutlined /></Tooltip>,
                key: 'favorite',
                render: (_, record) => <FavoriteStar id={record.id} isStarred={record._user?.in_use} toggleFavorite={toggleFavorite} />,
                align: 'center',
                width: 75
            },
            {
                title: 'Company',
                key: 'logo',
                render: (_) => <Logo src={formatCompanyLogoUrl()} height={'55px'} width={'55px'} />,
                align: 'center',
                width: 100
            },
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
                render: (text, record) => (
                    <Tooltip title={text}>
                        <span className={'line-clamp-2 max-w-[200px] font-medium'}>
                            {text}
                        </span>
                    </Tooltip>
                ),
                onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase()),
                searchable: true
            },
            {
                title: 'Type',
                dataIndex: 'type',
                key: 'type',
                align:'center',
                render: (type) => <div onClick={()=>setActiveFilters({[type]: true})}>
                    <Avatar size={'default'} shape={'square'} className={`${typeColors[type]}`} style={{borderRadius: '6px'}}>
                    <span className={`text-left ${type==='mm' && 'shadow-md'}`}>
                        {type === 'mm' ? <UserRound size={24} /> : PortfolioTypes[type]}
                    </span>
                </Avatar>
                </div>,
            },
            ...commonTableColumns({benchmarkTickers}),
            {
            title: 'Country',
            dataIndex: ['tags', 'region'],
            key: 'country',
            render: (region, record) => <CountryFlag country={record?.tags?.country} />,
            align: 'center',
        },
            {
                title: 'Tags',
                dataIndex: 'tags',
                key: 'tags',
                width: 250,
           render: (tags) => {
                const tagsToKeep = ['asset_class', 'style', 'size'];

                return (
                    <div className="grid grid-cols-3 gap-1">
                        {tagsToKeep.map((tagType) => {
                            const tagValue = tags?.[tagType];

                            if (!tagValue || tagValue.length === 0) {
                                return <TagPlaceholder key={tagType} type={tagType} />;
                            }

                            return (
                                <Tag
                                    key={tagType}
                                    className={`
                                        bg-gradient-to-b 
                                        ${getTagColors(tagType)} 
                                        shadow-sm 
                                        text-xs 
                                        py-0.5
                                        text-center
                                        rounded-lg
                                        truncate
                                        capitalize
                                    `}
                                    bordered={false}
                                     // Add tooltip for truncated text
                                >
                                    {tagValue}
                                </Tag>
                            );
                        })}
                    </div>
                );
            },
                align: 'left'
            }
    ], [onEdit, toggleFavorite]); // Add dependencies

}

export default useTableColumns;
